/* «Ещё» — профиль, разделы, утилиты и оформление.
   Оформление — Strong Inset List из Konsta: блоки без заголовков,
   строки без описаний, слева иконка Ionicons на цветной подложке. */

import { moreVisible } from "@mera/shared";
import { List, ListItem } from "konsta/react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "../lib/router.jsx";

import { Ion, IconTile } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { useApp } from "../lib/store.jsx";
import { getThemePref, onThemeChange, resolvedScheme, setThemePref, THEMES } from "../lib/theme.js";
import { haptic } from "../lib/tg.js";

/* Цвета подложек — как в настройках iOS: декоративные, не финансовая семантика */
const TILE = {
  profile: "#8e8e93",
  people: "#5856d6",
  admin: "var(--fin-gold)",
  expense: "#ff9500",
  business: "var(--tg-button)",
  goals: "var(--fin-gold)",
  reports: "#ff9500",
  meetings: "#5856d6",
  instagram: "#c13584",
  pdf: "#ff9500",
  passwords: "#30b0c7",
  cards: "#a2845e",
  shortcut: "#af52de",
  auto: "#8e8e93",
  light: "#ff9500",
  dark: "#5856d6",
};

/* Список рисуем цветами темы Telegram, а не чёрным/белым из палитры Konsta */
const ITEM_COLORS = {
  primaryTextIos: "text-[color:var(--tg-text)]",
  secondaryTextIos: "text-[color:var(--tg-hint)]",
};

const THEME_TILE = {
  auto: { icon: Ion.phonePortraitSolid, bg: TILE.auto },
  light: { icon: Ion.sunnySolid, bg: TILE.light },
  dark: { icon: Ion.moonSolid, bg: TILE.dark },
};

const Check = () => <Ion.checkmark size={20} style={{ color: "var(--tg-button)" }} />;

const SECTIONS = [
  { path: "/reports", label: "Отчёты", icon: Ion.barChartSolid, bg: TILE.reports },
];

/* Справочники: каждый открывается своим экраном со списком и кнопкой «+».
   Счетов здесь нет — они живут в настройках своего бизнеса, а проектов нет вовсе:
   проект заводится вместе со сделкой и живёт в CRM, своим справочником он не был. */
const MANAGE_SECTIONS = [
  { kind: "users", label: "Люди", icon: Ion.personSolid, bg: TILE.people },
  { kind: "businesses", label: "Бизнесы и счета", icon: Ion.briefcaseSolid, bg: TILE.business },
  { kind: "goals", label: "Цели", icon: Ion.flagSolid, bg: TILE.goals },
  { kind: "recurring", label: "Регулярные расходы", icon: Ion.repeatSolid, bg: TILE.expense },
];

/* Утилиты — то, что уже работает */
const TOOLS = [
  { path: "/meetings", label: "Саммери встреч", icon: Ion.headsetSolid, bg: TILE.meetings },
  { path: "/instagram", label: "Скачать из Instagram", icon: Ion.logoInstagram, bg: TILE.instagram },
  { path: "/pdf", label: "Сжатие PDF", icon: Ion.documentTextSolid, bg: TILE.pdf },
];

/* Хранилище доступов — свой блок: это не утилиты, а то, что команда хранит */
const VAULT = [
  { path: "/passwords", label: "Пароли и ссылки", icon: Ion.keySolid, bg: TILE.passwords },
  { path: "/cards", label: "Карты", icon: Ion.cardSolid, bg: TILE.cards },
];

/* Шорткат стоит отдельным блоком: это не утилита внутри мини-аппа,
   а вынос быстрой команды на телефон */
const SHORTCUT = { path: "/shortcut", label: "Шорткат", icon: Ion.flashSolid, bg: TILE.shortcut };

/* Блок ссылок: все разделы «Ещё» устроены одинаково — иконка на подложке и переход */
const LinkList = ({ items, navigate }) => (
  <List strong inset>
    {items.map((s) => (
      <ListItem
        key={s.path}
        link
        colors={ITEM_COLORS}
        media={<IconTile icon={s.icon} bg={s.bg} />}
        title={s.label}
        onClick={() => { haptic.light(); navigate(s.path); }}
      />
    ))}
  </List>
);

/* Выбор темы: авто (как в Telegram) · светлая · тёмная */
function ThemeList() {
  const [pref, setPref] = useState(getThemePref);
  const [, setScheme] = useState(resolvedScheme);

  useEffect(() => onThemeChange(setScheme), []);

  const pick = (id) => {
    haptic.select();
    setPref(id);
    setScheme(setThemePref(id));
  };

  return (
    <List strong inset>
      {THEMES.map((t) => {
        const tile = THEME_TILE[t.id];
        return (
          <ListItem
            key={t.id}
            link
            chevron={false}
            colors={ITEM_COLORS}
            media={<IconTile icon={tile.icon} bg={tile.bg} />}
            title={t.label}
            after={pref === t.id ? <Check /> : null}
            onClick={() => pick(t.id)}
          />
        );
      })}
    </List>
  );
}

export default function More() {
  const { me } = useApp();
  const navigate = useNavigate();

  return (
    <Screen logo>
      <List strong inset>
        <ListItem
          colors={ITEM_COLORS}
          media={<IconTile icon={Ion.personSolid} bg={TILE.profile} />}
          title={me?.name || "—"}
          after={me?.isAdmin ? "админ" : me?.role || "участник команды"}
        />
      </List>

      {/* Справочники — только администраторам (ТЗ 3) */}
      {me?.isAdmin ? (
        <List strong inset>
          {MANAGE_SECTIONS.map((s) => (
            <ListItem
              key={s.kind}
              link
              colors={ITEM_COLORS}
              media={<IconTile icon={s.icon} bg={s.bg} />}
              title={s.label}
              onClick={() => { haptic.light(); navigate(`/manage/${s.kind}`); }}
            />
          ))}
        </List>
      ) : null}

      {/* Блоки ниже админ выключает участнику в его карточке — тогда их здесь нет */}
      {moreVisible(me, "reports") ? <LinkList items={SECTIONS} navigate={navigate} /> : null}
      {moreVisible(me, "tools") ? <LinkList items={TOOLS} navigate={navigate} /> : null}
      {moreVisible(me, "vault") ? <LinkList items={VAULT} navigate={navigate} /> : null}
      {moreVisible(me, "shortcut") ? <LinkList items={[SHORTCUT]} navigate={navigate} /> : null}

      <ThemeList />
    </Screen>
  );
}
